import React, { useEffect, useState } from "react";
import ReactECharts from "echarts-for-react";
import { Box, Stack, Grid, ListItem, List } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import BarChartComponent from "./BarChartComponent";
import MultiBarChartComponent from "./MultiBarChartComponent";
import {
  effectivenessCategories,
  effectivenessPersonalCategories,
  effectivenessPersonalMapping,
  effectivenessPersonalValue,
  equalityCategories,
  equalityCategoriesValue,
  equalityMapping,
  safetyCategories,
  safetyMapping,
  safetyValue,
  yesNoMapping,
  yesNoValue,
} from "./OTBConstant";
import PaiChartComponent from "./PaiChartComponent";
import { DataSummary } from "../../assessmentsView/physicalAssessment/PhysicalDashboardPDFCommonComponent";

const OCA = ({ data, pdf }) => {
  const classes = useOTBStyle();

  const groupBy = (key) =>
    data?.reduce((acc, item) => {
      const value = item[key];
      acc[value] = acc[value] || [];
      acc[value].push(item);
      return acc;
    }, {});

  // const groupBy = (keyPath) =>
  //   data?.reduce((acc, item) => {
  //     const value = keyPath.split(".").reduce((obj, key) => obj?.[key], item);
  //     if (!value) return acc;

  //     acc[value] = acc[value] || [];
  //     acc[value].push(item);
  //     return acc;
  //   }, {});

  const controlQuestionnaireData = Object.entries(
    groupBy("control_questionnaire") || {}
  )
    ?.map(([key, items]) => ({
      name: key,
      value: items.length,
    }))
    .filter((item) => item.name !== "null");

  const CountbyShiftData = Object.entries(groupBy("shift") || {})
    ?.map(([key, items]) => ({
      name: key,
      value: items.length,
    }))
    .filter((item) => item.name !== "null");

  const RoundchartOption = {
    legend: {
      orient: "horizontal",
      bottom: 50,
      left: "center",
      textStyle: {
        fontSize: 16,
        fontWeight: "bold",
        color: "#000",
      },
    },
    toolbox: {
      show: true,
    },
    series: [
      {
        name: "Nightingale Chart",
        type: "pie",
        radius: [100, 180],
        center: ["50%", "50%"],
        roseType: "area",
        itemStyle: {
          borderRadius: 8,
        },
        data: CountbyShiftData,
        label: {
          show: true,
          formatter: "{c}: {b}",
          textStyle: {
            fontWeight: "bold",
            fontSize: 14,
          },
        },
        color: ["#B29263", "#D5B98B", "#E8D0B1", "#B3B3B3"],
        animation: false,
        animationDuration: 0,
        animationDurationUpdate: 0,
      },
    ],
  };

  const calculatePercentageData = (categories, keyWord, valueCounts) => {
    categories?.forEach((category) => {
      data?.forEach((item) => {
        const value = item[category];
        if (value && value?.result) {
          const numericResult = parseInt(value.result, 10);
          if (valueCounts[numericResult] !== undefined) {
            valueCounts[numericResult] += 1;
          }
        }
      });
    });

    const totalEntries = Object?.values(valueCounts)?.reduce(
      (sum, count) => sum + count,
      0
    );

    const percentageData = Object?.keys(valueCounts)?.map((key) => {
      const percentage = ((valueCounts[key] / totalEntries) * 100).toFixed(0);
      return {
        name: keyWord[key],
        value: percentage === "0" ? 0 : parseInt(percentage, 10),
      };
    });

    return percentageData;
  };

  const equalityPieChartData = calculatePercentageData(
    equalityCategories,
    equalityMapping,
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 }
  );
  const effectivenessPieChartData = calculatePercentageData(
    effectivenessCategories,
    equalityMapping,
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0, 7: 0 }
  );
  const effectivenessPersonalPieChartData = calculatePercentageData(
    effectivenessPersonalCategories,
    effectivenessPersonalMapping,
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 }
  );
  const safetyPieChartData = calculatePercentageData(
    safetyCategories,
    safetyMapping,
    { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  );

  return (
    <Box>
      {pdf && (
        <Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              Organizational Climate Assessment
            </VSNTypography>

            <Stack gap={2} direction="row">
              <ReactECharts
                option={{
                  grid: {
                    top: 20,
                    right: 20,
                    bottom: 20,
                  },
                  title: {
                    text: "Control Questionnaire",
                    left: "center",
                    top: 30,
                  },
                  tooltip: { trigger: "item" },
                  legend: { bottom: 30 },
                  color: [
                    "#C3A577",
                    "#E0BE94",
                    "#E8D0B1",
                    "#F1E1CD",
                    "#B3B3B3",
                    "#808080",
                  ],
                  series: [
                    {
                      name: "Control Questionnaire",
                      type: "pie",
                      radius: [0, 130],
                      label: {
                        show: true,
                        formatter: "{b}: {c}",
                        textStyle: {
                          fontWeight: "bold",
                          fontSize: 14,
                        },
                      },
                      data: controlQuestionnaireData,
                      animation: false,
                      animationDuration: 0,
                      animationDurationUpdate: 0,
                    },
                  ],
                }}
                style={{ height: "600px", width: "550px" }}
              />

              <ReactECharts
                option={RoundchartOption}
                style={{ height: "600px", width: "550px" }}
              />
            </Stack>
            <Stack direction="row" gap={2}>
              <Box
                sx={{
                  padding: "16px",
                  backgroundColor: "#F1E1CD",
                  borderRadius: "12px",
                  width: "200px",
                }}
              >
                <VSNTypography sx={{}}>Margin of Error</VSNTypography>
                <VSNTypography
                  textAlign="center"
                  fontSize="24px"
                  fontWeight={700}
                >
                  5.7%
                </VSNTypography>
              </Box>
              <Box
                sx={{
                  padding: "16px",
                  backgroundColor: "#F1E1CD",
                  borderRadius: "12px",
                }}
              >
                <VSNTypography fontSize="14px">
                  Margin of Error (MOE) reflects the inherent uncertainty due to
                  sampling variability or size relative to total articipants.
                  Smaller MOEs provide more precise estimates.
                </VSNTypography>
                <VSNTypography fontSize="14px">
                  Using a 95% confidence interval, this means with 95% certainty
                  we can say results are plus/minus your MOE score.
                </VSNTypography>
              </Box>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">
              Organizational Climate Assessment
            </VSNTypography>
            <Stack gap={2} direction="row">
              <Stack direction="column" className={classes.pdfChartCard}>
                <BarChartComponent
                  data={data}
                  keyValue="leadership_visibility"
                  height="230px"
                  width="510px"
                />
                <Box px={2}>
                  <VSNTypography variant="h2">
                    Leadership Visibility
                  </VSNTypography>
                  <VSNTypography variant="h3">
                    On average, how many times in the last 6 months have you
                    seen your Commander/Senior leader at your section/station?
                  </VSNTypography>
                </Box>
              </Stack>

              <Stack direction="column" className={classes.pdfChartCard}>
                <BarChartComponent
                  data={data}
                  keyValue="hazing"
                  height="230px"
                  width="510px"
                />
                <Box px={2}>
                  <VSNTypography variant="h2">Hazing</VSNTypography>
                  <VSNTypography variant="h3">
                    Individuals in my workplace are pressured to engage in the
                    following acts as part of an initiation or admission process
                    without a proper governmental purpose.
                  </VSNTypography>
                </Box>
              </Stack>
            </Stack>

            <Stack gap={2} direction="row" pt={1}>
              <Stack direction="column" className={classes.pdfChartCard}>
                <BarChartComponent
                  data={data}
                  keyValue="targeting"
                  height="230px"
                  width="510px"
                />
                <Box px={2}>
                  <VSNTypography variant="h2">Targeting</VSNTypography>
                  <VSNTypography variant="h3">
                    Individuals in my workplace who are seen as “different” are
                    targets
                  </VSNTypography>
                </Box>
              </Stack>

              <Stack direction="column" className={classes.pdfChartCard}>
                <BarChartComponent
                  data={data}
                  keyValue="suicide"
                  height="230px"
                  width="510px"
                />
                <Box px={2}>
                  <VSNTypography variant="h2">Suicide</VSNTypography>
                  <VSNTypography variant="h3">
                    In the past 12 months, I have known someone in my
                    organization who has thought of, attempted, or died by
                    suicide.
                  </VSNTypography>
                </Box>
              </Stack>
            </Stack>
          </Box>
          {/* -------equality------------ */}
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always" }}
          >
            <VSNTypography variant="h4">Equality</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["equality_1", "equality_2", "equality_3"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>

              <Stack direction="row" gap={2}>
                {["equality_4", "equality_5", "equality_6"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>

          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "10px" }}
          >
            <VSNTypography variant="h4">Equality</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["equality_7", "equality_8", "equality_9"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {["equality_10", "equality_11", "equality_12"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "10px" }}
          >
            <VSNTypography variant="h4">Equality</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["equality_13", "equality_14", "equality_15"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {["equality_16", "equality_17", "equality_18"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "10px" }}
          >
            <VSNTypography variant="h4">Equality</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["equality_19", "equality_20", "equality_21"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="348px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>

              <Stack direction="row" gap={2}>
                <MultiBarChartComponent
                  data={data}
                  categories={equalityCategoriesValue}
                  nameMapping={equalityMapping}
                  keyValue="equality_22"
                  height="280px"
                  width="348px"
                  pdf
                />

                <PaiChartComponent
                  data={equalityPieChartData}
                  height="350px"
                  width="750px"
                  pdf
                  title="Overall Comparison"
                />
              </Stack>
            </Stack>
          </Box>
          {/* --------equality_workplace------------ */}
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "5px" }}
          >
            <VSNTypography variant="h4">Equality</VSNTypography>
            <VSNTypography variant="h6">
              While under your current senior leader and within the last 12
              months, did someone from your workplace:
            </VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {[
                  "equality_workplace_1",
                  "equality_workplace_2",
                  "equality_workplace_3",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="275px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>

              <Stack direction="row" gap={2}>
                {["equality_workplace_4", "equality_workplace_5"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="275px"
                    width="500px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          {/* --------Effectiveness------------ */}
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "10px" }}
          >
            <VSNTypography variant="h4">Effectiveness</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["effectiveness_1", "effectiveness_2", "effectiveness_3"].map(
                  (key) => (
                    <MultiBarChartComponent
                      data={data}
                      categories={equalityCategoriesValue}
                      nameMapping={equalityMapping}
                      keyValue={key}
                      height="280px"
                      width="350px"
                      pdf
                      key={key}
                    />
                  )
                )}
              </Stack>

              <Stack direction="row" gap={2}>
                {["effectiveness_26", "effectiveness_5", "effectiveness_6"].map(
                  (key) => (
                    <MultiBarChartComponent
                      data={data}
                      categories={equalityCategoriesValue}
                      nameMapping={equalityMapping}
                      keyValue={key}
                      height="280px"
                      width="350px"
                      pdf
                      key={key}
                    />
                  )
                )}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "10px" }}
          >
            <VSNTypography variant="h4">Effectiveness</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["effectiveness_7", "effectiveness_8", "effectiveness_9"].map(
                  (key) => (
                    <MultiBarChartComponent
                      data={data}
                      categories={equalityCategoriesValue}
                      nameMapping={equalityMapping}
                      keyValue={key}
                      height="280px"
                      width="350px"
                      pdf
                      key={key}
                    />
                  )
                )}
              </Stack>

              <Stack direction="row" gap={2}>
                {[
                  "effectiveness_10",
                  "effectiveness_11",
                  "effectiveness_12",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "10px" }}
          >
            <VSNTypography variant="h4">Effectiveness</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {[
                  "effectiveness_13",
                  "effectiveness_14",
                  "effectiveness_15",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>

              <Stack direction="row" gap={2}>
                {[
                  "effectiveness_16",
                  "effectiveness_17",
                  "effectiveness_18",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Effectiveness</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {[
                  "effectiveness_19",
                  "effectiveness_20",
                  "effectiveness_21",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>

              <Stack direction="row" gap={2}>
                {[
                  "effectiveness_22",
                  "effectiveness_23",
                  "effectiveness_24",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Effectiveness</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <MultiBarChartComponent
                  data={data}
                  categories={equalityCategoriesValue}
                  nameMapping={equalityMapping}
                  keyValue="effectiveness_25"
                  height="280px"
                  width="540px"
                  pdf
                />
                {/* <MultiBarChartComponent
                data={data}
                categories={equalityCategoriesValue}
                nameMapping={equalityMapping}
                keyValue="effectiveness_26"
                height="280px"
                width="540px"
                pdf
              /> */}
              </Stack>

              <Stack direction="row" gap={2}>
                <PaiChartComponent
                  data={effectivenessPieChartData}
                  height="340px"
                  width="900px"
                  pdf
                  title="Overall Comparison"
                />
              </Stack>
            </Stack>
          </Box>
          {/* --------effectiveness_personal------------ */}
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Effectiveness</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {[
                  "effectiveness_personal_1",
                  "effectiveness_personal_2",
                  "effectiveness_personal_3",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={effectivenessPersonalValue}
                    nameMapping={effectivenessPersonalMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>

              <Stack direction="row" gap={2}>
                <MultiBarChartComponent
                  data={data}
                  categories={effectivenessPersonalValue}
                  nameMapping={effectivenessPersonalMapping}
                  keyValue="effectiveness_personal_4"
                  height="280px"
                  width="350px"
                  pdf
                />

                <PaiChartComponent
                  data={effectivenessPersonalPieChartData}
                  height="340px"
                  width="800px"
                  pdf
                  title="Overall Comparison"
                />
              </Stack>
            </Stack>
          </Box>
          {/* -------- safety ------------ */}
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Safety</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["safety_1", "safety_2", "safety_3"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {["safety_4", "safety_5", "safety_6"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Safety</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["safety_7", "safety_8", "safety_9"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {["safety_10", "safety_11", "safety_12"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Safety</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["safety_13", "safety_14", "safety_15"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {["safety_16", "safety_17", "safety_18"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Safety</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {["safety_19", "safety_20", "safety_21"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {["safety_22", "safety_23", "safety_24"].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="280px"
                    width="350px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Safety</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                <PaiChartComponent
                  data={safetyPieChartData}
                  height="400px"
                  width="700px"
                  pdf
                  title="Overall Comparison"
                />
              </Stack>
            </Stack>
          </Box>
          {/* -------- leadership ------------ */}
          <Box
            className={classes.pdfReportLayout}
            sx={{ pageBreakAfter: "always", marginTop: "15px" }}
          >
            <VSNTypography variant="h4">Leadership</VSNTypography>

            <Stack gap={2}>
              <Stack direction="row" gap={2}>
                {[
                  "leadership_3",
                  "leadership_4",
                  "leadership_5",
                  "leadership_6",
                ].map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="260px"
                    width="250px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
              <Stack direction="row" gap={2}>
                {[
                  "leadership_7",
                  "leadership_8",
                  "leadership_1",
                  "leadership_2",
                ]?.map((key) => (
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="260px"
                    width="250px"
                    pdf
                    key={key}
                  />
                ))}
              </Stack>
            </Stack>
          </Box>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Organizational Climate Assessment
              </VSNTypography>
            </div>

            <Grid container spacing={4}>
              <Grid item xs={12} xlg={6} smd={6}>
                <Box maxWidth={400} mx="auto">
                  <DataSummary
                    title="Control Questionnaire"
                    data={data}
                    field="control_questionnaire"
                  />
                </Box>
                <Box sx={{ height: { lg: 550, md: 450, xs: 350 } }}>
                  <ReactECharts
                    option={{
                      tooltip: { trigger: "item" },
                      legend: {
                        bottom: 50,
                        textStyle: {
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#000",
                        },
                      },
                      color: [
                        "#C3A577",
                        "#E0BE94",
                        "#E8D0B1",
                        "#F1E1CD",
                        "#B3B3B3",
                        "#808080",
                      ],
                      series: [
                        {
                          name: "Control Questionnaire",
                          type: "pie",
                          radius: "60%",
                          data: controlQuestionnaireData,
                          animation: false,
                          animationDuration: 0,
                          animationDurationUpdate: 0,
                        },
                      ],
                    }}
                    style={{ height: "100%", width: "100%" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} xlg={6} smd={6}>
                <Box maxWidth={400} mx="auto">
                  <DataSummary
                    title="Count by Shift"
                    data={data}
                    field="shift"
                  />
                </Box>
                <Box sx={{ height: { lg: 550, md: 450, xs: 400 } }}>
                  <ReactECharts
                    option={RoundchartOption}
                    style={{ height: "100%", width: "100%" }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} xlg={12}>
                <Stack direction={{ lg: "row", sm: "column" }} gap={2}>
                  <Box
                    p={{ lg: 3, xs: 2 }}
                    sx={{
                      backgroundColor: "#F1E1CD",
                      borderRadius: "12px",
                      width: "fit-content",
                      marginRight: "auto",
                    }}
                  >
                    <VSNTypography>Margin of Error</VSNTypography>
                    <VSNTypography variant="h4">5.7%</VSNTypography>
                  </Box>
                  <Box
                    p={{ lg: 3, xs: 2 }}
                    sx={{
                      backgroundColor: "#F1E1CD",
                      borderRadius: "12px",
                    }}
                  >
                    <List
                      disablePadding
                      sx={{
                        listStyle: "disc",
                        pl: 2,
                        "& .MuiListItem-root": { display: "list-item" },
                      }}
                    >
                      <ListItem disableGutters>
                        Margin of Error (MOE) reflects the inherent uncertainty
                        due to sampling variability or size relative to total
                        articipants. Smaller MOEs provide more precise
                        estimates.
                      </ListItem>
                      <ListItem disableGutters>
                        Using a 95% confidence interval, this means with 95%
                        certainty we can say results are plus/minus your MOE
                        score.
                      </ListItem>
                    </List>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">
                Organizational Climate Assessment
              </VSNTypography>
            </div>
            {[
              {
                subTitle: "Leadership Visibility",
                data,
                keyValue: "leadership_visibility",
              },
              {
                subTitle: "Hazing",
                data,
                keyValue: "hazing",
              },
              {
                subTitle: "Targeting",
                data,
                keyValue: "targeting",
              },
              {
                subTitle: "Suicide",
                data,
                keyValue: "suicide",
              },
            ]?.map(({ subTitle, data, keyValue }, index) => {
              return (
                <Box key={index} mb={2}>
                  <Box mb={1}>
                    <VSNTypography variant="h6">{subTitle}</VSNTypography>
                    <VSNTypography variant="body1">
                      {data?.find((item) => item[keyValue]?.question)?.[
                        keyValue
                      ]?.question || "No question available"}
                    </VSNTypography>
                  </Box>

                  <Grid container spacing={4}>
                    <Grid item xs={12}>
                      <BarChartComponent
                        data={data}
                        keyValue={keyValue}
                        title={subTitle}
                        notPdf
                      />
                    </Grid>
                  </Grid>
                </Box>
              );
            })}
          </Box>

          {/* -------equality------------ */}

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">Equality</VSNTypography>
            </div>
            <Grid container spacing={4} mb={2}>
              {["equality_1", "equality_2", "equality_3"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["equality_4", "equality_5", "equality_6"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["equality_7", "equality_8", "equality_9"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["equality_10", "equality_11", "equality_12"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["equality_13", "equality_14", "equality_15"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["equality_16", "equality_17", "equality_18"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["equality_19", "equality_20", "equality_21"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              <Grid item xs={12} lg={4}>
                <MultiBarChartComponent
                  data={data}
                  categories={equalityCategoriesValue}
                  nameMapping={equalityMapping}
                  keyValue="equality_22"
                  height="380px"
                  width="100%"
                />
              </Grid>
              <Grid item xs={12} lg={8}>
                <Box sx={{ height: 400 }}>
                  <PaiChartComponent
                    data={equalityPieChartData}
                    height="400px"
                    width="100%"
                    title="Overall Comparison"
                  />
                </Box>
              </Grid>
            </Grid>
            <Box>
              <VSNTypography variant="h6">
                While under your current senior leader and within the last 12
                months, did someone from your workplace:
              </VSNTypography>
            </Box>

            <Grid container spacing={4} mb={2}>
              {[
                "equality_workplace_1",
                "equality_workplace_2",
                "equality_workplace_3",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Box>
              <VSNTypography variant="h6">
                While under your current senior leader and within the last 12
                months, did someone from your workplace:
              </VSNTypography>
            </Box>

            <Grid container spacing={4}>
              {["equality_workplace_4", "equality_workplace_5"]?.map((key) => (
                <Grid item xs={12} lg={6} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* --------Effectiveness------------ */}
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">Effectiveness</VSNTypography>
            </div>

            <Grid container spacing={4} mb={2}>
              {["effectiveness_1", "effectiveness_2", "effectiveness_3"]?.map(
                (key) => (
                  <Grid item xs={12} lg={4} key={key}>
                    <MultiBarChartComponent
                      data={data}
                      categories={equalityCategoriesValue}
                      nameMapping={equalityMapping}
                      keyValue={key}
                      height="380px"
                      width="100%"
                    />
                  </Grid>
                )
              )}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["effectiveness_26", "effectiveness_5", "effectiveness_6"]?.map(
                (key) => (
                  <Grid item xs={12} lg={4} key={key}>
                    <MultiBarChartComponent
                      data={data}
                      categories={equalityCategoriesValue}
                      nameMapping={equalityMapping}
                      keyValue={key}
                      height="380px"
                      width="100%"
                    />
                  </Grid>
                )
              )}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["effectiveness_7", "effectiveness_8", "effectiveness_9"]?.map(
                (key) => (
                  <Grid item xs={12} lg={4} key={key}>
                    <MultiBarChartComponent
                      data={data}
                      categories={equalityCategoriesValue}
                      nameMapping={equalityMapping}
                      keyValue={key}
                      height="380px"
                      width="100%"
                    />
                  </Grid>
                )
              )}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {[
                "effectiveness_10",
                "effectiveness_11",
                "effectiveness_12",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {[
                "effectiveness_13",
                "effectiveness_14",
                "effectiveness_15",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {[
                "effectiveness_16",
                "effectiveness_17",
                "effectiveness_18",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {[
                "effectiveness_19",
                "effectiveness_20",
                "effectiveness_21",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {[
                "effectiveness_22",
                "effectiveness_23",
                "effectiveness_24",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={equalityCategoriesValue}
                    nameMapping={equalityMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              <Grid item xs={12} lg={4}>
                <MultiBarChartComponent
                  data={data}
                  categories={equalityCategoriesValue}
                  nameMapping={equalityMapping}
                  keyValue="effectiveness_25"
                  height="380px"
                  width="100%"
                />
              </Grid>

              {/* <Grid item xs={12} lg={4}>
              <MultiBarChartComponent
                data={data}
                categories={equalityCategoriesValue}
                nameMapping={equalityMapping}
                keyValue="effectiveness_26"
              />
            </Grid> */}
              <Grid item xs={12} lg={8}>
                <Box sx={{ height: 400 }}>
                  <PaiChartComponent
                    data={effectivenessPieChartData}
                    height="400px"
                    width="100%"
                    title="Overall Comparison"
                  />
                </Box>
              </Grid>
            </Grid>
            <Grid container spacing={4} mb={2}>
              {[
                "effectiveness_personal_1",
                "effectiveness_personal_2",
                "effectiveness_personal_3",
              ]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={effectivenessPersonalValue}
                    nameMapping={effectivenessPersonalMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              <Grid item xs={12} lg={4}>
                <MultiBarChartComponent
                  data={data}
                  categories={effectivenessPersonalValue}
                  nameMapping={effectivenessPersonalMapping}
                  keyValue="effectiveness_personal_4"
                  height="380px"
                  width="100%"
                />
              </Grid>

              <Grid item xs={12} lg={8}>
                <Box sx={{ height: 400 }}>
                  <PaiChartComponent
                    data={effectivenessPersonalPieChartData}
                    height="400px"
                    width="100%"
                    title="Overall Comparison"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* -------- safety ------------ */}

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">Safety</VSNTypography>
            </div>

            <Grid container spacing={4} mb={2}>
              {["safety_1", "safety_2", "safety_3"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_4", "safety_5", "safety_6"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_7", "safety_8", "safety_9"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_10", "safety_11", "safety_12"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_13", "safety_14", "safety_15"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_16", "safety_17", "safety_18"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_19", "safety_20", "safety_21"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["safety_22", "safety_23", "safety_24"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={safetyValue}
                    nameMapping={safetyMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4}>
              <Grid item xs={12} lg={12}>
                <Box sx={{ height: "400px" }}>
                  <PaiChartComponent
                    data={safetyPieChartData}
                    height="400px"
                    width="100%"
                    title="Overall Comparison"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* -------- leadership ------------ */}

          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">Leadership</VSNTypography>
            </div>

            <Grid container spacing={4} mb={2}>
              {["leadership_1", "leadership_2", "leadership_3"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["leadership_4", "leadership_5", "leadership_6"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
            <Grid container spacing={4} mb={2}>
              {["leadership_7", "leadership_8"]?.map((key) => (
                <Grid item xs={12} lg={4} key={key}>
                  <MultiBarChartComponent
                    data={data}
                    categories={yesNoValue}
                    nameMapping={yesNoMapping}
                    keyValue={key}
                    height="380px"
                    width="100%"
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default OCA;
